import type { Condition, ConditionMap } from './types';

/**
 * Evaluates a condition against a context.
 * Handles both named conditions (string) and inline function conditions.
 *
 * @param condition - The condition to evaluate (string name or inline function)
 * @param context - The context to evaluate against
 * @param conditions - Map of named conditions (required for string conditions)
 * @returns true if the condition passes, false otherwise
 *
 * @example
 * // Named condition
 * evaluateCondition('canAccessQuotation', context, conditions);
 *
 * // Inline function condition
 * evaluateCondition((ctx) => ctx.someFlag, context, conditions);
 */
export function evaluateCondition<TContext>(
    condition: Condition<TContext>,
    context: TContext,
    conditions: ConditionMap<TContext>,
): boolean {
    if (typeof condition === 'string') {
        const conditionFn = conditions[condition];
        if (!conditionFn) {
            console.warn(`Unknown condition: "${condition}". Returning false.`);
            return false;
        }
        return conditionFn(context);
    }
    return condition(context);
}

/**
 * Evaluates multiple conditions with AND logic.
 * All conditions must pass for the result to be true.
 *
 * @param conditionList - Array of conditions to evaluate
 * @param context - The context to evaluate against
 * @param conditions - Map of named conditions
 * @returns true if all conditions pass, false otherwise
 *
 * @example
 * evaluateAllConditions(
 *     ['canAccessApp', 'canAccessQuotation'],
 *     context,
 *     conditions
 * );
 */
export function evaluateAllConditions<TContext>(
    conditionList: Condition<TContext>[],
    context: TContext,
    conditions: ConditionMap<TContext>,
): boolean {
    return conditionList.every(condition =>
        evaluateCondition(condition, context, conditions),
    );
}

/**
 * Evaluates multiple conditions with OR logic.
 * At least one condition must pass for the result to be true.
 *
 * @param conditionList - Array of conditions to evaluate
 * @param context - The context to evaluate against
 * @param conditions - Map of named conditions
 * @returns true if any condition passes, false otherwise
 *
 * @example
 * evaluateAnyCondition(
 *     ['canAccessSso', 'canAccessLogin', 'canAccessApp'],
 *     context,
 *     conditions
 * );
 */
export function evaluateAnyCondition<TContext>(
    conditionList: Condition<TContext>[],
    context: TContext,
    conditions: ConditionMap<TContext>,
): boolean {
    return conditionList.some(condition =>
        evaluateCondition(condition, context, conditions),
    );
}
